// src/pages/AccessDenied/roleRoutePermissions.js
//
// Sistema de control de acceso por ruta apoyado en /api/MasterData/role-modules.
//
// El backend devuelve para cada (employeeId, roleId) un array con TODOS los
// módulos del catálogo y un flag `hasAccess`. Ese array vive en
// `user.modules` (cargado en AuthContext.runPostLoginFlow / switchRole).
//
// Este archivo expone canRoleAccessRoute(user, pathname). Para decidir si una
// ruta está permitida:
//   1. Si la ruta es UNIVERSAL → permitir.
//   2. Si el rol activo es Admin/CEO → permitir (safety net; en condiciones
//      normales el RBAC matrix también les concede todos los módulos).
//   3. Mapear la ruta a un moduleKey del catálogo (TAB_MODULES). Si la ruta
//      no está mapeada → permitir (no hay gate).
//   4. Buscar el moduleKey en user.modules. hasAccess = true → permitir;
//      cualquier otra cosa → denegar.
//
// Si admin agrega un módulo nuevo al catálogo y le da acceso a un rol vía
// la matriz RBAC, basta con agregar el prefijo de ruta correspondiente al
// map ROUTE_TO_MODULE_KEY usando el mismo `moduleKey` que aparece en
// Administration → Access Control → Modules.

const UNIVERSAL_ROUTES = [
  "/Main",
  "/main",
  "/profile",
  "/patient-profiles",
  "/support",
  "/Login",
  "/Unauthorized",
  "/AccessDenied",
  "/library",
];

// Roles que tienen bypass de control de acceso. En el ideal el backend les
// concede todos los módulos vía la matriz RBAC y este bypass es redundante,
// pero se conserva como red de seguridad para no dejar a admin/CEO bloqueados
// si alguien revoca un módulo por error.
const FULL_ACCESS_ROLE_KEYS = new Set(["admin", "ceo"]);
const FULL_ACCESS_ROLE_IDS = new Set(["1", "4"]);

// Map de prefijos de ruta a moduleKey del catálogo (TAB_MODULES).
//
// IMPORTANTE: cada string a la derecha debe coincidir EXACTAMENTE con el
// `moduleKey` definido por el admin en Administration → Modules. Si un
// módulo no aparece aquí, su ruta queda abierta a cualquier usuario
// autenticado (no recomendado para módulos sensibles).
const ROUTE_TO_MODULE_KEY = {
  "/care-plans": "care_plans",
  "/Careplan": "care_plans",
  "/patient-map": "patient_map",
  "/PatientMap": "patient_map",
  "/patientMap": "patient_map",
  "/patient-detail": "patient_map",
  "/clinical-info": "clinical_info",
  "/ClinicalInfo": "clinical_info",
  "/clinicalInfo": "clinical_info",
  // High Cost es módulo independiente pero reusa el moduleKey clinical_info
  // (quien tenía acceso a Clinical Info conserva High Cost). Cambiar a un key
  // propio "high_cost" cuando el backend lo agregue a la matriz RBAC.
  "/high-cost": "clinical_info",
  // No Recent Visits es módulo independiente pero reusa el moduleKey
  // clinical_info (lista clínica de priorización de pacientes). Cambiar a un
  // key propio "no_recent_visits" cuando el backend lo agregue a la matriz RBAC.
  "/no-recent-visits": "clinical_info",
  "/care-gaps": "care_gaps",
  "/CareGap": "care_gaps",
  "/coding-opportunities": "coding_opportunities",
  "/coding-opportunities-v2": "coding_opportunities",
  "/CoddingOpportunities": "coding_opportunities",
  "/specialist-referrals": "specialist_referrals",
  "/SpecialistReferralForm": "specialist_referrals",
  "/SpecialistReferralDashboard": "specialist_referrals",
  "/lab-phlebotomy": "lab_phlebotomy",
  "/LabPhlebotomy": "lab_phlebotomy",
  "/ClinicalNoteAi": "clinical_note_ai",
  "/CreateClinicalNote": "clinical_notes",
  "/ClinicalNotesList": "clinical_notes",
  "/CreateClinicalNotePage": "clinical_notes",
  "/clinical-notes": "clinical_notes",
  "/MedicationReconciliationPage": "clinical_notes",
  // Patient Geography (api/geography) — intencionalmente SIN moduleKey: los SPs
  // todavía loguean con NUM_ID_MODULE = 1 y el módulo no está en el catálogo
  // (pendiente #4 del doc 11 de GeoSistem). Mapear a "patient_geography" cuando
  // el backend lo registre. Mientras tanto el sidebar lo gatea con PATIENTS_READ.
  "/assignments": "assignments",
  "/Assignment": "assignments",
  "/administration": "administration",
  // Tools agrupados (todas las herramientas operativas comparten el mismo
  // módulo del catálogo). Si en el futuro se separan por subherramienta,
  // basta agregar más entradas aquí con su propio moduleKey.
  "/Tools": "tools",
  "/AHCA": "tools",
  "/PrePostAudi": "tools",
  "/PCPChanges": "tools",
  "/AddOn": "tools",
  "/NewMember": "tools",
  "/PostVisit": "tools",
  "/ACAAging6265": "tools",
  "/ACAAging65": "tools",
  "/WelcomeCampaign": "tools",
  "/ReportGeneratorPage": "tools",
  "/ClinicalDataCollectorPage": "tools",
  "/DataQualityValidationPage": "tools",
  "/report": "tools",
  // Etapa 2 — BillingV2 (módulos con badge "Develop"). Intencionalmente SIN
  // moduleKey mientras estén en desarrollo con fake data: quedan abiertos a
  // cualquier usuario autenticado. Cuando el backend agregue sus módulos al
  // catálogo (TAB_MODULES), mapear aquí cada prefijo a su moduleKey:
  //   /cdci, /charge-capture, /post-discharge, /high-risk-surveillance,
  //   /medication-adherence, /previsit-planning, /provider-doc-coach,
  //   /denials-management, /member-outreach, /member-onboarding,
  //   /membership-analytics, /executive-financial, /clinical-operations,
  //   /provider-performance
};

const matchesPrefix = (pathname, route) =>
  pathname === route || pathname.startsWith(route + "/");

// Devuelve el moduleKey requerido para una ruta dada. Si más de un prefijo
// coincide, gana el más largo (match más específico).
const getRequiredModuleKey = (pathname) => {
  let bestPrefix = null;
  Object.keys(ROUTE_TO_MODULE_KEY).forEach((route) => {
    if (matchesPrefix(pathname, route)) {
      if (!bestPrefix || route.length > bestPrefix.length) bestPrefix = route;
    }
  });
  return bestPrefix ? ROUTE_TO_MODULE_KEY[bestPrefix] : null;
};

const isUniversalRoute = (pathname) =>
  UNIVERSAL_ROUTES.some((r) => matchesPrefix(pathname, r));

const isFullAccessRole = (user) => {
  if (!user) return false;
  if (FULL_ACCESS_ROLE_IDS.has(String(user.rol))) return true;
  const roleKey = user.activeRole?.key
    ? String(user.activeRole.key).toLowerCase()
    : null;
  return roleKey ? FULL_ACCESS_ROLE_KEYS.has(roleKey) : false;
};

/**
 * Verifica si el usuario puede acceder a una ruta.
 *
 * @param {Object} user - user object de AuthContext (incluye activeRole y modules)
 * @param {string} pathname - location.pathname
 * @returns {boolean}
 */
export const canUserAccessRoute = (user, pathname) => {
  if (isUniversalRoute(pathname)) return true;
  if (isFullAccessRole(user)) return true;

  const requiredModuleKey = getRequiredModuleKey(pathname);
  // Ruta sin módulo asociado: no hay gate, queda abierta a cualquier
  // usuario autenticado.
  if (!requiredModuleKey) return true;

  const modules = Array.isArray(user?.modules) ? user.modules : [];
  // Si el user todavía no tiene módulos (post-login en curso o usuario
  // multi-rol que aún no eligió rol) no bloqueamos: ProtectedRoute /
  // LoginPage manejarán el siguiente paso.
  if (modules.length === 0) return true;

  const found = modules.find(
    (m) => m && m.key && String(m.key).toLowerCase() === requiredModuleKey
  );
  return Boolean(found?.hasAccess);
};

/**
 * Compatibilidad con la firma antigua. La nueva lógica vive en
 * canUserAccessRoute(user, pathname); este wrapper queda para no romper
 * callers existentes mientras se migran.
 *
 * @param {string|number} userRol - legacy user.rol
 * @param {string} pathname
 * @returns {boolean}
 */
export const canRoleAccessRoute = (userRol, pathname) => {
  return canUserAccessRoute({ rol: userRol }, pathname);
};
